// Route templates for the `ui.route_change` telemetry event (services/routeCapture.js).
//
// A template names WHERE the user is without carrying any value from the URL: path
// segments that are ids become `:id`, and the query keeps its parameter names only,
// sorted.
import { matchCopyRoute } from "../copyLinks/route.js";

/** First path segments that name a page; anything else is a value. */
const PAGE_WORDS = new Set(["copy", "dev"]);
/** The `/dev/<page>` fixture pages App.jsx renders; any other second segment is a value. */
const DEV_PAGES = new Set([
  "link-landing",
  "card-states",
  "settings-states",
  "share-dialog",
  "resolution-matrix",
]);
/** A query parameter name worth keeping; any other name becomes `*`. */
const PARAM_NAME_RE = /^[A-Za-z_][A-Za-z0-9_-]{0,31}$/;

/** The path of `pathname` as a template: `/copy/<id>` → `/copy/:id`, `/` stays `/`. */
export function pathTemplate(pathname: string): string {
  if (matchCopyRoute(pathname) !== null) return "/copy/:id";
  const segs = pathname.split("/");
  return segs
    .map((seg, i) => {
      if (seg === "") return seg;
      if (i === 1 && PAGE_WORDS.has(seg)) return seg;
      if (i === 2 && segs[1] === "dev" && DEV_PAGES.has(seg)) return seg;
      return ":id";
    })
    .join("/");
}

/** The query of `search` as its sorted, de-duplicated parameter names (`?a&b`), or "". */
export function searchTemplate(search: string): string {
  const names = new Set<string>();
  for (const name of new URLSearchParams(search).keys()) {
    names.add(PARAM_NAME_RE.test(name) ? name : "*");
  }
  if (names.size === 0) return "";
  return `?${[...names].sort().join("&")}`;
}

/** `href` (absolute, or rooted at the app) as `path?names`, or null when it does not parse. */
export function routeTemplateOf(href: string | null | undefined): string | null {
  if (typeof href !== "string") return null;
  let u: URL;
  try {
    u = new URL(href, "http://x.invalid");
  } catch {
    return null;
  }
  return `${pathTemplate(u.pathname)}${searchTemplate(u.search)}`;
}
